import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/common/common-components/ui/Card";
import { Input } from "@/app/common/common-components/ui/Input";
import { Label } from "@/app/common/common-components/ui/Label";
import { RadioGroup, RadioGroupItem } from "@/app/common/common-components/ui/RadioGroup";
import { formatNumber, formatInputNumber } from "@/app/common/common-functions/functions";

interface VatCalculatorProps {
  countryCode?: string;
  currencySymbol?: string;
}

export const VatCalculator = ({ countryCode = "RSA", currencySymbol = "R" }: VatCalculatorProps) => {
  const [amount, setAmount] = useState("");
  const [vatRate, setVatRate] = useState(15);
  const [calculationType, setCalculationType] = useState<"add" | "exclude">("add");

  const handleNumberInput = (value: string, setter: (value: string) => void) => {
    // Only allow numbers and spaces
    const numericValue = value.replace(/[^0-9 ]/g, "");
    setter(numericValue);
  };

  const numAmount = parseFloat(amount.replace(/ /g, "")) || 0;
  const valid = amount !== "";

  const calculateVat = () => {
    if (!valid) return { vatAmount: 0, totalAmount: 0 };
    if (calculationType === "add") {
      const vatAmount = numAmount * (vatRate / 100);
      return {
        vatAmount,
        totalAmount: numAmount + vatAmount,
      };
    } else {
      const totalAmount = numAmount;
      const vatAmount = totalAmount * (vatRate / (100 + vatRate));
      return {
        vatAmount,
        totalAmount: totalAmount - vatAmount,
      };
    }
  };

  const { vatAmount, totalAmount } = calculateVat();

  return (
    <Card>
      <CardHeader>
        <CardTitle>VAT Calculator</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="tw-flex tw-flex-col tw-gap-8 md:tw-flex-row">
          <div className="tw-w-full md:tw-w-1/2">
            <div className="tw-space-y-4">
              <div className="tw-space-y-2">
                <Label htmlFor="amount">Amount ({currencySymbol})</Label>
                <Input
                  id="amount"
                  type="text"
                  value={formatInputNumber(amount)}
                  onChange={(e) => handleNumberInput(e.target.value, setAmount)}
                  placeholder="Enter amount"
                  className="tw-w-full"
                />
              </div>
              <div className="tw-space-y-2">
                <Label htmlFor="vatRate">VAT Rate (%)</Label>
                <Input id="vatRate" type="number" value={vatRate} onChange={(e) => setVatRate(parseFloat(e.target.value))} className="tw-w-full" />
              </div>
              <RadioGroup value={calculationType} onValueChange={(value) => setCalculationType(value as "add" | "exclude")} className="tw-flex tw-space-x-4">
                <div className="tw-flex tw-items-center tw-space-x-2">
                  <RadioGroupItem value="add" id="add" />
                  <Label htmlFor="add">Add VAT</Label>
                </div>
                <div className="tw-flex tw-items-center tw-space-x-2">
                  <RadioGroupItem value="exclude" id="exclude" />
                  <Label htmlFor="exclude">Exclude VAT</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          <div className="tw-mt-8 tw-w-full md:tw-mt-0 md:tw-w-1/2">
            <div className="tw-space-y-4 tw-rounded-lg tw-bg-muted tw-p-4">
              <div className="tw-text-lg tw-font-semibold">Results</div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-font-medium">VAT Amount:</span>
                <span className="tw-font-semibold">
                  {currencySymbol} {formatNumber(vatAmount)}
                </span>
              </div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-font-medium">Total Amount:</span>
                <span className="tw-font-semibold">
                  {currencySymbol} {formatNumber(totalAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
